import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  User, Upload, FileText, Sparkles, Heart, Target, Globe,
  Star, Brain, Users, ArrowRight
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';
import UniversalHeartToggle from './UniversalHeartToggle';

const PersonalManifestoOnboarding = ({ player, onComplete }) => {
  const [avatarName, setAvatarName] = useState(player?.name || '');
  const [personalObjective, setPersonalObjective] = useState('');
  const [earthObjective, setEarthObjective] = useState('');
  const [selectedPersonalOptions, setSelectedPersonalOptions] = useState([]);
  const [selectedEarthOptions, setSelectedEarthOptions] = useState([]);
  const [uploadedAvatar, setUploadedAvatar] = useState(null);
  const [uploadedManifesto, setUploadedManifesto] = useState(null);
  const [wantsToCreateGuild, setWantsToCreateGuild] = useState(false);
  const [spiralPhase, setSpiralPhase] = useState(0);
  
  const avatarFileRef = useRef(null);
  const manifestoFileRef = useRef(null);

  // Grant spiral animation
  React.useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  const personalOptions = [
    "Create meaningful work that serves both personal growth and collective wellbeing",
    "Build authentic relationships and find my true collaborative partners",
    "Develop my creative gifts while contributing to community abundance",
    "Heal family patterns and create generational wisdom for my lineage",
    "Launch projects that align with my deepest values and vision",
    "Find balance between individual fulfillment and service to others"
  ];

  const earthOptions = [
    "Support regenerative practices that heal our relationship with nature",
    "Foster authentic human connection and community resilience",
    "Create abundance systems that work for all beings and future generations",
    "Bridge ancient wisdom with modern innovation for planetary healing",
    "Facilitate consciousness expansion and awakening for collective evolution",
    "Build trust-based economies that honor both individual and collective needs"
  ];

  const handleComplete = () => {
    if (!avatarName.trim()) {
      alert('Please choose an avatar name');
      return;
    }

    const manifestoData = {
      avatar_name: avatarName,
      personal_objective: personalObjective || selectedPersonalOptions.join('; '),
      earth_objective: earthObjective || selectedEarthOptions.join('; '),
      wants_to_create_guild: wantsToCreateGuild,
      uploaded_avatar: uploadedAvatar,
      uploaded_manifesto: uploadedManifesto,
      created_at: new Date().toISOString(),
      world_set_participant: true
    };

    // Store personal manifesto
    localStorage.setItem('personal_manifesto', JSON.stringify(manifestoData));

    // Enhanced user data with manifesto integration
    const enhancedPlayer = {
      ...player,
      manifesto: manifestoData,
      consciousness_level: 'manifesto_activated',
      ready_for_gameplay: true,
      guild_creation_requested: wantsToCreateGuild
    };

    console.log('🌟 PERSONAL MANIFESTO COMPLETE:', manifestoData);
    
    if (wantsToCreateGuild) {
      // Go to guild creation page
      localStorage.setItem('pending_guild_creation', JSON.stringify(enhancedPlayer));
      alert('🏰 Guild Creation: You will now design your guild manifesto and invite system!');
      // TODO: Navigate to guild creation page
      onComplete(enhancedPlayer);
    } else {
      onComplete(enhancedPlayer);
    }
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedAvatar(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleManifestoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedManifesto(event.target.result);
        setPersonalObjective(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_mairy-chat/artifacts/r47m1ygo_DA63FE00-9235-4124-AE03-E55E266118BE.webp)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(12) rotate(${spiralPhase}deg)`,
            filter: 'blur(4px) saturate(1.6) brightness(1.3)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={400} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/20 to-black/60"></div>

      <div className="relative z-10 min-h-screen p-6 flex items-center justify-center">
        <div className="w-full max-w-4xl mx-auto">
          
          {/* Header - Emphasize Optional */}
          <div className="text-center mb-8">
            <h1 
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.8)'
              }}
            >
              Your Personal Manifesto
            </h1>
            <p 
              className="text-cyan-200 text-xl"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Help M.ai.ry understand your deepest intentions (optional)
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Left Column: Avatar & Core Questions */}
            <div className="space-y-6">
              
              {/* Avatar Selection */}
              <Card className="bg-black/60 backdrop-blur-xl border border-purple-400/30 shadow-2xl">
                <CardContent className="p-6">
                  <h3 className="text-white text-lg font-bold mb-4">Choose Your Seeker Agent Name</h3>
                  
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={avatarName}
                      onChange={(e) => setAvatarName(e.target.value)}
                      placeholder="Your avatar name in the consciousness field..."
                      className="w-full p-4 bg-purple-800/50 border border-purple-400/50 rounded-xl text-white placeholder-purple-200 text-center"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    />
                    
                    {/* Avatar Upload */}
                    <div className="text-center">
                      <Button
                        onClick={() => avatarFileRef.current?.click()}
                        variant="outline"
                        className="text-purple-300 border-purple-400/50"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Avatar Image
                      </Button>
                      <input
                        ref={avatarFileRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        className="hidden"
                      />
                      {uploadedAvatar && (
                        <div className="mt-4">
                          <img 
                            src={uploadedAvatar} 
                            alt="Avatar preview"
                            className="w-20 h-20 rounded-full mx-auto border-2 border-purple-400/50"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Personal Objectives */}
              <Card className="bg-black/60 backdrop-blur-xl border border-cyan-400/30 shadow-2xl">
                <CardContent className="p-6">
                  <h3 className="text-white text-lg font-bold mb-4">
                    1. Personal & Family Objectives
                  </h3>
                  <p className="text-cyan-100 text-sm mb-4">
                    Share your broad intentions for yourself or your family. These are creative-commons appropriate ideas that help game master M.ai.ry understand how to best support your journey.
                  </p>
                  
                  <textarea
                    value={personalObjective}
                    onChange={(e) => setPersonalObjective(e.target.value)}
                    placeholder=""
                    className="w-full p-4 bg-cyan-800/30 border border-cyan-400/50 rounded-xl text-white placeholder-cyan-200 resize-none"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    rows={4}
                  />
                  
                  {/* Quick Select Options */}
                  <div className="mt-4">
                    <p className="text-cyan-200 text-xs mb-2">Or select a general intention:</p>
                    <div className="space-y-2">
                      {personalOptions.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedPersonalOptions(prev => 
                              prev.includes(option) 
                                ? prev.filter(opt => opt !== option)
                                : [...prev, option]
                            );
                          }}
                          className={`w-full text-left p-2 rounded-lg border text-xs transition-all ${
                            selectedPersonalOptions.includes(option)
                              ? 'bg-cyan-500/30 border-cyan-400 text-cyan-200'
                              : 'bg-black/30 border-cyan-400/30 text-cyan-300 hover:bg-cyan-500/20'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded border ${
                              selectedPersonalOptions.includes(option) 
                                ? 'bg-cyan-400 border-cyan-400' 
                                : 'border-cyan-400/50'
                            }`} />
                            <span>{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column: Earth Objectives & Upload */}
            <div className="space-y-6">
              
              {/* Earth Objectives */}
              <Card className="bg-black/60 backdrop-blur-xl border border-green-400/30 shadow-2xl">
                <CardContent className="p-6">
                  <h3 className="text-white text-lg font-bold mb-4">
                    2. Earth & Humanity Objectives
                  </h3>
                  <p className="text-green-100 text-sm mb-4">
                    Share your vision for our planet and humanity. These general ideas help shape how the game supports collective flourishing alongside personal growth.
                  </p>
                  
                  <textarea
                    value={earthObjective}
                    onChange={(e) => setEarthObjective(e.target.value)}
                    placeholder="Share your vision for planetary healing and collective flourishing..."
                    className="w-full p-4 bg-green-800/30 border border-green-400/50 rounded-xl text-white placeholder-green-200 resize-none"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    rows={4}
                  />
                  
                  {/* Quick Select Options */}
                  <div className="mt-4">
                    <p className="text-green-200 text-xs mb-2">Or select a planetary intention:</p>
                    <div className="space-y-2">
                      {earthOptions.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedEarthOptions(prev => 
                              prev.includes(option) 
                                ? prev.filter(opt => opt !== option)
                                : [...prev, option]
                            );
                          }}
                          className={`w-full text-left p-2 rounded-lg border text-xs transition-all ${
                            selectedEarthOptions.includes(option)
                              ? 'bg-green-500/30 border-green-400 text-green-200'
                              : 'bg-black/30 border-green-400/30 text-green-300 hover:bg-green-500/20'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded border ${
                              selectedEarthOptions.includes(option) 
                                ? 'bg-green-400 border-green-400' 
                                : 'border-green-400/50'
                            }`} />
                            <span>{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Manifesto Upload */}
              <Card className="bg-black/60 backdrop-blur-xl border border-yellow-400/30 shadow-2xl">
                <CardContent className="p-6">
                  <h3 className="text-white text-lg font-bold mb-4">Upload Your Manifesto</h3>
                  <p className="text-yellow-100 text-sm mb-4">
                    Optional: Upload a text file with your personal vision or manifesto
                  </p>
                  
                  <Button
                    onClick={() => manifestoFileRef.current?.click()}
                    variant="outline"
                    className="w-full text-yellow-300 border-yellow-400/50 hover:bg-yellow-500/20"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Upload Manifesto (.txt file)
                  </Button>
                  
                  <input
                    ref={manifestoFileRef}
                    type="file"
                    accept=".txt,text/plain"
                    onChange={handleManifestoUpload}
                    className="hidden"
                  />
                  
                  {uploadedManifesto && (
                    <div className="mt-4 p-3 bg-yellow-500/20 rounded-lg border border-yellow-400/30">
                      <p className="text-yellow-200 text-xs">
                        📄 Manifesto uploaded and integrated into your personal ledger
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* World-Set Vision */}
              <Card className="bg-black/60 backdrop-blur-xl border border-purple-400/30 shadow-2xl">
                <CardContent className="p-6">
                  <h3 className="text-purple-200 text-lg font-bold mb-3">The World-Set Vision</h3>
                  <p className="text-purple-100 text-sm leading-relaxed">
                    We're not just creating a new headset for individual experience. 
                    We're taking the collective reality of shared perception and bringing it into the real world. 
                    At the end of this journey, people accomplish their bliss, find the right collaborators, 
                    get help organizing on this platform, and the most remembering of us are invited to 
                    play in the soil and dirt at our rustic consciousness resort - a model we will take around the world.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Guild Creation Option */}
          <div className="text-center mt-8 mb-8">
            <Card className="bg-gradient-to-r from-yellow-900/60 to-orange-900/60 backdrop-blur-xl border border-yellow-400/30 shadow-2xl max-w-2xl mx-auto">
              <CardContent className="p-8">
                <h3 
                  className="text-yellow-200 text-2xl font-bold mb-4"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  🏰 Start Your Own Guild?
                </h3>
                <p className="text-yellow-100 text-sm mb-6 leading-relaxed">
                  Would you like to create a guild or group of consciousness agents using a common manifesto? 
                  This allows you to play semi-independently with your own community while integrating with the larger field.
                </p>
                
                <div className="flex space-x-4 justify-center">
                  <Button
                    onClick={() => setWantsToCreateGuild(true)}
                    className={`px-8 py-3 ${
                      wantsToCreateGuild 
                        ? 'bg-yellow-500 text-black font-bold' 
                        : 'bg-yellow-500/30 text-yellow-200 border border-yellow-400/50'
                    }`}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Yes, Create Guild
                  </Button>
                  
                  <Button
                    onClick={() => setWantsToCreateGuild(false)}
                    className={`px-8 py-3 ${
                      !wantsToCreateGuild 
                        ? 'bg-purple-500 text-white font-bold' 
                        : 'bg-purple-500/30 text-purple-200 border border-purple-400/50'
                    }`}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Join Existing Community
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Complete Onboarding - Optional Enhancement */}
          <div className="text-center mt-8">
            <div className="flex justify-center space-x-6">
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white px-12 py-6 text-xl font-bold"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <ArrowRight className="w-6 h-6 mr-3" />
                Enter with Manifesto
              </Button>
              
              <Button
                onClick={() => {
                  // Skip manifesto - M.ai.ry will build it over time
                  const minimalPlayer = {
                    ...player,
                    manifesto: {
                      avatar_name: avatarName || player.name,
                      personal_objective: "To be discovered through gameplay",
                      earth_objective: "To be discovered through gameplay",
                      build_over_time: true
                    },
                    consciousness_level: 'discovering',
                    ready_for_gameplay: true
                  };
                  onComplete(minimalPlayer);
                }}
                variant="outline"
                className="text-white border-white/40 hover:bg-white/10 px-8 py-6 text-lg"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Skip - Build Over Time
              </Button>
            </div>
            
            <p className="text-white/60 text-sm mt-4 italic">
              Your manifesto becomes part of your consciousness expansion journey, whether entered now or discovered through play
            </p>
          </div>
        </div>
      </div>
      
      <UniversalHeartToggle />
      <PersistentFooter />
    </div>
  );
};

export default PersonalManifestoOnboarding;